import { type NodePosition } from '../../hooks/commands/types';
import { type NodeRect } from './nodeLayout';

/** Minimum number of nodes required for spacing distribution */
const MIN_NODES_FOR_DISTRIBUTION = 3;

/** The outer bounding rectangle of a set of node rects */
export type SelectionBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

type AlignOptions = {
  nodes: NodeRect[];
  /** When provided, align to these bounds instead of deriving them from the nodes. */
  bounds?: SelectionBounds;
};

/**
 * Computes the outer bounding rectangle of a set of node rects.
 * Used by alignment functions and the bounding box overlay.
 */
export function computeSelectionBounds(nodes: NodeRect[]): SelectionBounds {
  if (nodes.length === 0) {
    return { height: 0, maxX: 0, maxY: 0, minX: 0, minY: 0, width: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.width);
    maxY = Math.max(maxY, node.y + node.height);
  }

  return { height: maxY - minY, maxX, maxY, minX, minY, width: maxX - minX };
}

/** Align all nodes' left edges to the leftmost node's left edge, or to bounds.minX */
export function alignLeft({ nodes, bounds }: AlignOptions): NodePosition[] {
  if (nodes.length < 2 && !bounds) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const targetX = bounds ? bounds.minX : Math.min(...nodes.map((n) => n.x));

  return nodes.map((n) => ({ id: n.id, position: { x: targetX, y: n.y } }));
}

/** Align all nodes' right edges to the rightmost node's right edge, or to bounds.maxX */
export function alignRight({ nodes, bounds }: AlignOptions): NodePosition[] {
  if (nodes.length < 2 && !bounds) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const targetRight = bounds ? bounds.maxX : Math.max(...nodes.map((n) => n.x + n.width));

  return nodes.map((n) => ({ id: n.id, position: { x: targetRight - n.width, y: n.y } }));
}

/** Align all nodes' top edges to the topmost node's top edge, or to bounds.minY */
export function alignTop({ nodes, bounds }: AlignOptions): NodePosition[] {
  if (nodes.length < 2 && !bounds) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const targetY = bounds ? bounds.minY : Math.min(...nodes.map((n) => n.y));

  return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: targetY } }));
}

/** Align all nodes' bottom edges to the bottommost node's bottom edge, or to bounds.maxY */
export function alignBottom({ nodes, bounds }: AlignOptions): NodePosition[] {
  if (nodes.length < 2 && !bounds) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const targetBottom = bounds ? bounds.maxY : Math.max(...nodes.map((n) => n.y + n.height));

  return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: targetBottom - n.height } }));
}

/** Align all nodes' horizontal centers to the horizontal center of the bounding box or provided bounds */
export function alignHorizontalCenter({ nodes, bounds }: AlignOptions): NodePosition[] {
  if (nodes.length < 2 && !bounds) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const refBounds = bounds ?? computeSelectionBounds(nodes);
  const centerX = refBounds.minX + refBounds.width / 2;

  return nodes.map((n) => ({ id: n.id, position: { x: centerX - n.width / 2, y: n.y } }));
}

/** Align all nodes' vertical centers to the vertical center of the bounding box or provided bounds */
export function alignVerticalCenter({ nodes, bounds }: AlignOptions): NodePosition[] {
  if (nodes.length < 2 && !bounds) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const refBounds = bounds ?? computeSelectionBounds(nodes);
  const centerY = refBounds.minY + refBounds.height / 2;

  return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: centerY - n.height / 2 } }));
}

type DistributeOptions = {
  nodes: NodeRect[];
};

/**
 * Distributes nodes so the horizontal spacing between them is even.
 * The leftmost and rightmost nodes (by left edge) stay in place.
 * Requires 3+ nodes; fewer returns unchanged positions.
 */
export function distributeHorizontalSpacing({ nodes }: DistributeOptions): NodePosition[] {
  if (nodes.length < MIN_NODES_FOR_DISTRIBUTION) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const sorted = [...nodes].sort((a, b) => {
    const dx = a.x - b.x;

    return dx !== 0 ? dx : a.x + a.width / 2 - (b.x + b.width / 2);
  });

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalSpace = last.x + last.width - first.x;
  const totalObjectWidth = sorted.reduce((sum, n) => sum + n.width, 0);
  const spacing = (totalSpace - totalObjectWidth) / (sorted.length - 1);

  let currentX = first.x + first.width;

  return sorted.map((n, i) => {
    if (i === 0) {
      return { id: n.id, position: { x: n.x, y: n.y } };
    }
    if (i === sorted.length - 1) {
      return { id: n.id, position: { x: last.x, y: n.y } };
    }
    const newX = currentX + spacing;
    currentX = newX + n.width;
    return { id: n.id, position: { x: newX, y: n.y } };
  });
}

/**
 * Distributes nodes so the vertical spacing between them is even.
 * The topmost and bottommost nodes (by top edge) stay in place.
 * Requires 3+ nodes; fewer returns unchanged positions.
 */
export function distributeVerticalSpacing({ nodes }: DistributeOptions): NodePosition[] {
  if (nodes.length < MIN_NODES_FOR_DISTRIBUTION) {
    return nodes.map((n) => ({ id: n.id, position: { x: n.x, y: n.y } }));
  }

  const sorted = [...nodes].sort((a, b) => {
    const dy = a.y - b.y;

    return dy !== 0 ? dy : a.y + a.height / 2 - (b.y + b.height / 2);
  });

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const totalSpace = last.y + last.height - first.y;
  const totalObjectHeight = sorted.reduce((sum, n) => sum + n.height, 0);
  const spacing = (totalSpace - totalObjectHeight) / (sorted.length - 1);

  let currentY = first.y + first.height;

  return sorted.map((n, i) => {
    if (i === 0) {
      return { id: n.id, position: { x: n.x, y: n.y } };
    }
    if (i === sorted.length - 1) {
      return { id: n.id, position: { x: n.x, y: last.y } };
    }
    const newY = currentY + spacing;
    currentY = newY + n.height;
    return { id: n.id, position: { x: n.x, y: newY } };
  });
}
